import { getPublicEnv } from "@janeway/app-sdk/public-env";

/**
 * Der Merkzettel: welche Suite-Apps auf DIESEM Geraet installiert sind.
 *
 * **Warum es ihn ueberhaupt gibt.** Die Hub-Uebersicht moechte pro Zeile ein
 * Haekchen zeigen. Sie kann das nicht selbst herausfinden: `beforeinstallprompt`
 * und `getInstalledRelatedApps()` gelten nur fuer die EIGENE Herkunft, und der
 * Speicher eines fremden Ursprungs ist in einem iframe nach Herkunft getrennt.
 * Eine Seite auf `hub.janeway.io` kann also prinzipiell nicht wissen, ob
 * `mail.janeway.io` installiert ist. Also sagt es die App selbst: sie schreibt
 * ihren Namen in ein Cookie auf der Parent-Domain, das alle Subdomains lesen.
 *
 * **EIN Cookie mit Kommaliste, nicht eines pro App.** Der Wert faehrt bei jeder
 * Anfrage an jede App mit, Bilder und Chunks eingeschlossen — dieselbe Sorge,
 * die `THEME_OVERRIDES_COOKIE_MAX` schon dokumentiert. 23 Einzel-Cookies waeren
 * 23 Zeilen in der Cookie-Richtlinie und 23-mal Kopfzeilen-Last.
 *
 * **Er ist ein Merkzettel, keine Wahrheit** — und die UI sagt das auch so:
 * - Nach einer Deinstallation raeumt niemand auf; das Haekchen bleibt stehen.
 *   Dagegen gibt es den Weg „stimmt nicht" (`vergissInstalliert`).
 * - Auf iOS hat eine Homescreen-App einen EIGENEN Cookie-Topf. Was sie dort
 *   schreibt, sieht Safari nie. Auf iOS bleibt die Liste also ohne Haekchen.
 *
 * Nichts haengt daran: fehlt der Merkzettel, fehlt ein Haekchen. Der
 * Install-Weg selbst fragt ihn nie.
 */
export const MERKZETTEL_COOKIE = "jw_pwa";

/**
 * 30 Tage — dieselbe Frist wie die geteilten Darstellungs-Cookies, und dieselbe
 * Begruendung: der Eintrag setzt allein eine Handlung des Nutzers um (er hat
 * installiert), und was er in einem Klick neu herstellt, rechtfertigt keine
 * laengere Speicherung (Cookie-Richtlinie Ziff. 2, § 25 Abs. 2 Nr. 2 TDDDG).
 */
export const MERKZETTEL_MAX_AGE = 60 * 60 * 24 * 30;

/** Obergrenze der Liste. Die Suite hat gut 20 installierbare Apps. */
export const MERKLISTE_MAX = 40;

/** Ein Subdomain-Label: genau das, was vor dem ersten Punkt steht. */
const SUB_MUSTER = /^[a-z0-9-]{1,32}$/;

/**
 * Der eigene Name, abgeleitet aus dem Hostnamen.
 *
 * **Bewusst abgeleitet und nicht konfiguriert:** die App schreibt, die
 * Hub-Liste liest, und beide nehmen denselben Weg. Ein Konfigurationswert
 * koennte auseinanderlaufen — dieser kann es nicht, weil es keinen gibt.
 */
export function eigenerSub(hostname: string | null | undefined): string {
  if (!hostname) return "";
  // Eine nackte IP hat kein Subdomain-Label. Ohne diesen Fall waere "127" der
  // Name der App.
  if (/^\d{1,3}(\.\d{1,3}){3}$/.test(hostname)) return "";
  const label = hostname.toLowerCase().split(".")[0] ?? "";
  return SUB_MUSTER.test(label) ? label : "";
}

/**
 * Liest die Liste aus einem `Cookie`-Kopf oder aus `document.cookie`.
 *
 * Gefiltert wird trotz eigener Herkunft: was hier durchkaeme, landete als Text
 * in der Liste und als Teil einer URL.
 */
export function leseMerkliste(cookieKopf: string | null | undefined): string[] {
  if (!cookieKopf) return [];
  const treffer = cookieKopf.match(
    new RegExp(`(?:^|; )${MERKZETTEL_COOKIE}=([^;]*)`),
  );
  if (!treffer) return [];
  return merklisteAusWert(treffer[1]);
}

/**
 * Dieselbe Liste aus dem bloßen Cookie-WERT.
 *
 * Der Server bekommt von `cookies().get(...)` nur den Wert, nicht den ganzen
 * Kopf — und genau dort soll die Hub-Uebersicht ihn lesen, damit die Haekchen
 * schon im ersten Bild stehen und kein `useEffect` sie nachtraegt.
 */
export function merklisteAusWert(wert: string | null | undefined): string[] {
  if (!wert) return [];
  let roh: string;
  try {
    roh = decodeURIComponent(wert);
  } catch {
    // Ein von Hand verdrehter Wert ist kein Ausfall, nur ein leerer Merkzettel.
    return [];
  }
  return roh
    .split(",")
    .map((t) => t.trim().toLowerCase())
    .filter((t) => SUB_MUSTER.test(t));
}

/**
 * Nimmt einen Eintrag auf. Der NEUE steht vorn — wird gekappt, faellt der
 * aelteste weg, nicht der, den der Nutzer gerade eben installiert hat.
 */
export function merklisteMit(vorhanden: readonly string[], sub: string): string[] {
  if (!SUB_MUSTER.test(sub)) return [...vorhanden];
  const rest = vorhanden.filter((t) => t !== sub && SUB_MUSTER.test(t)).sort();
  return [sub, ...rest].slice(0, MERKLISTE_MAX);
}

export function merklisteOhne(vorhanden: readonly string[], sub: string): string[] {
  return vorhanden.filter((t) => t !== sub);
}

export function serialisiereMerkliste(subs: readonly string[]): string {
  return subs.join(",");
}

function parentDomain(): string | undefined {
  return getPublicEnv("NEXT_PUBLIC_COOKIE_DOMAIN");
}

function schreibe(subs: readonly string[]): void {
  if (typeof document === "undefined") return;
  const domain = parentDomain();
  if (domain) {
    // Derselbe Doppelgaenger-Fall wie bei den Theme-Cookies: ein host-only
    // Cookie gleichen Namens ueberschattet beim Lesen den geteilten. Ein
    // `Max-Age=0` OHNE `Domain` trifft ausschliesslich den host-only.
    document.cookie = `${MERKZETTEL_COOKIE}=; Path=/; Max-Age=0`;
  }
  const teile = [
    `${MERKZETTEL_COOKIE}=${encodeURIComponent(serialisiereMerkliste(subs))}`,
    "Path=/",
    `Max-Age=${MERKZETTEL_MAX_AGE}`,
    "SameSite=Lax",
  ];
  if (domain) teile.push(`Domain=${domain}`);
  if (typeof window !== "undefined" && window.location.protocol === "https:") {
    teile.push("Secure");
  }
  document.cookie = teile.join("; ");
}

/** Merkt sich, dass diese App auf diesem Geraet installiert ist. */
export function merkeInstalliert(sub: string): void {
  if (typeof document === "undefined" || !sub) return;
  schreibe(merklisteMit(leseMerkliste(document.cookie), sub));
}

/** Nimmt den Eintrag zurueck — der Weg hinter „stimmt nicht“ in der Hub-Liste. */
export function vergissInstalliert(sub: string): void {
  if (typeof document === "undefined" || !sub) return;
  const rest = merklisteOhne(leseMerkliste(document.cookie), sub);
  if (rest.length === 0) {
    const domain = parentDomain();
    document.cookie = `${MERKZETTEL_COOKIE}=; Path=/; Max-Age=0`;
    if (domain) document.cookie = `${MERKZETTEL_COOKIE}=; Path=/; Max-Age=0; Domain=${domain}`;
    return;
  }
  schreibe(rest);
}
